import React, {useEffect, useContext, useState, useMemo, useRef, useCallback} from 'react'
import './ReservationCalendar.scss'
import {TextField, InputAdornment, IconButton} from '@material-ui/core'
import {Calendar, momentLocalizer, Views} from 'react-big-calendar'
import {ReservationCalendarMainContext} from '../../context/ReservationCalendarMain'
import useCalendarViewContext from '../../context/CalendarView'
import moment from 'moment'
import BigCalendarToolbar from './BigCalendarToolbar'
import ReservationAddEvent from './ReservationAddEvent'
import _ from 'lodash'
import useReservationEventsReducer, {ActionTypes} from '../../state/reservation/events/reducer'
import {getReservationEvents, removeEventViaID} from '../../state/reservation/events/actions'
import usePop from '../../utils/usePop'
import CalendarEventReservationPopup from '../CalendarEventPopup'
import CalendarEventGroupDatePopup from '../CalendarEventGroupDatePopup'
import CalendarEventDateDrivenActionPopup from '../CalendarEventDateDrivenActionPopup'
import CalendarEventActionDatePopup from '../CalendarEventActionDatePopup'
import {sidebarDisplayMeta} from './constants'
import GlobalEvents from '../../utils/EventSystem'
import Grid from '@material-ui/core/Grid'
import CalendarRightSidebar from './CalendarRightSidebar'
import ClearIcon from '@material-ui/icons/Clear'

const localizer = momentLocalizer(moment) // or globalizeLocalizer

const ReservationCalendar = () => {
  const {
    selectedDate,
    calendarView,
    dateWiseData,
    setNavigatedDate,
    handleSidebarData,
    navigatedDate,
    realSearch,
    isSameDate,
    sidebarDateClicked,
    setSidebarDateClicked,
  } = useContext(ReservationCalendarMainContext)

  const {viewData, shareableViewData} = useCalendarViewContext()
  const [modal, setModal] = useState({
    show: false,
    event: {},
  })
  const stateCalEv = usePop()
  const [events, dispatch] = useReservationEventsReducer()

  const callDelay = useRef(null)
  const eventParamsCache = useRef({
    start_date: _.cloneDeep(selectedDate).startOf(`${calendarView}`),
    end_date: _.cloneDeep(selectedDate).endOf(`${calendarView}`),
    group_fields: [],
    entity_action_ids: [],
    field_filters: {},
  })

  const eventParamsCacheForShareable = useRef({
    start_date: _.cloneDeep(selectedDate).startOf(`${calendarView}`),
    end_date: _.cloneDeep(selectedDate).endOf(`${calendarView}`),
    group_field: [],
    entity_action_ids: [],
  })

  const tryCallApi = useCallback(
    params => {
      if (!!!params.entity_id || params.start_date === null || params.end_date === null) return
      if (callDelay.current) {
        clearTimeout(callDelay.current)
        callDelay.current = null
      }
      callDelay.current = setTimeout(() => {
        getReservationEvents(dispatch, params.entity_id, _.cloneDeep(viewData), params)
      }, 250)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, viewData]
  )

  // shareable view
  const tryCallShareableApi = useCallback(
    params => {
      if (!!!params.entity_id || params.start_date === null || params.end_date === null) return
      if (callDelay.current) {
        clearTimeout(callDelay.current)
        callDelay.current = null
      }
      callDelay.current = setTimeout(() => {
        getReservationEvents(dispatch, params.entity_id, _.cloneDeep(shareableViewData), params)
      }, 250)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, shareableViewData]
  )

  // shareable view
  useEffect(() => {
    if (
      !_.isEmpty(shareableViewData?.selectedView?.group_view) &&
      (calendarView === 'day' || calendarView === 'week' || calendarView === 'month')
    ) {
      dispatch({type: ActionTypes.SET_VIEW, payload: {shareableViewData, reset: true}})
      tryCallShareableApi(prepareParamsForShareableApi())
    }
    //eslint-disable-next-line
  }, [shareableViewData, tryCallShareableApi, dispatch])

  const prepareParamsForApi = () => {
    let groups = []
    let actions = []
    let filters = {}
    let startDate = _.cloneDeep(selectedDate).startOf(`${calendarView}`)
    let endDate = _.cloneDeep(selectedDate).endOf(`${calendarView}`)
    if (!_.isEmpty(viewData.selectedView.group_view.groups)) {
      groups = viewData.selectedView.group_view.groups.map(data => {
        if (data.active === true) {
          return data
        }
        return null
      })
    }
    if (!_.isEmpty(viewData.selectedView.group_view.actions)) {
      actions = viewData.selectedView.group_view.actions.map(data => {
        if (data.active === true) {
          return data.action_id
        }
        return null
      })
    }

    if (!_.isEmpty(viewData.selectedFilteredViews)) {
      let _filters = {}
      viewData.selectedFilteredViews.forEach(filteredView => {
        filteredView.filters.map(data => {
          if (Array.isArray(data.values)) {
            if (Object.keys(_filters).includes(data.key)) {
              _filters[`${data.key}`] = [
                ..._filters[`${data.key}`],
                ...data.values.map(abc => {
                  return abc.id
                }),
              ]
            } else {
              _filters[`${data.key}`] = data.values.map(abc => {
                return abc.id
              })
            }
          } else {
            if (Object.keys(_filters).includes(data.key)) {
              _filters[`${data.key}`] = [..._filters[`${data.key}`], data.values]
            } else {
              _filters[`${data.key}`] = data.values
            }
          }
          filters = {...filters, ..._filters}
          return filters
        })
      })
    }

    return (eventParamsCache.current = {
      ...eventParamsCache.current,
      start_date: startDate.format('YYYY-MM-DD'),
      end_date: endDate.format('YYYY-MM-DD'),
      group_fields: _.compact(groups),
      entity_action_ids: _.compact(actions),
      filters: filters,
      entity_id: viewData?.selectedView?.group_view?.entity_id,
    })
  }

  // shareable view
  const prepareParamsForShareableApi = () => {
    let groups = []
    let startDate = _.cloneDeep(selectedDate).startOf(`${calendarView}`)
    let endDate = _.cloneDeep(selectedDate).endOf(`${calendarView}`)
    if (calendarView === 'day') {
      endDate.add(1, 'day')
    }
    if (!_.isEmpty(shareableViewData.selectedView.group_view.groups)) {
      groups = shareableViewData.selectedView.group_view.groups.map(data => {
        if (data.active === true) {
          return data
        }
        return null
      })
    }
    return (eventParamsCacheForShareable.current = {
      ...eventParamsCacheForShareable.current,
      start_date: startDate.format('YYYY-MM-DD'),
      end_date: endDate.format('YYYY-MM-DD'),
      group_field: _.compact(groups),
      entity_action_ids: shareableViewData.selectedView.group_view.actions,
      multiple_filter_views: {},
      entity_id: shareableViewData?.selectedView?.group_view?.entity_id,
      query: realSearch,
      is_shareable_view: true,
      field_filters: shareableViewData?.selectedView?.group_view?.filters,
    })
  }

  useEffect(() => {
    if (
      !_.isEmpty(viewData?.selectedView) &&
      (calendarView === 'day' || calendarView === 'week' || calendarView === 'month')
    ) {
      dispatch({type: ActionTypes.SET_VIEW, payload: {viewData, reset: true}})
      tryCallApi(prepareParamsForApi())
    }
    //eslint-disable-next-line
  }, [viewData, tryCallApi, dispatch])

  useEffect(() => {
    if (
      !_.isEmpty(viewData?.selectedView) &&
      (calendarView === 'day' || calendarView === 'week' || calendarView === 'month')
    ) {
      dispatch({type: ActionTypes.SET_VIEW, payload: {viewData, reset: true}})
      tryCallApi(prepareParamsForApi())
    }
    //eslint-disable-next-line
  }, [viewData, tryCallApi, dispatch])

  useEffect(() => {
    if (
      !_.isEmpty(viewData?.selectedView) &&
      (calendarView === 'day' || calendarView === 'week' || calendarView === 'month')
    ) {
      const startDate = _.cloneDeep(selectedDate).startOf(`${calendarView}`)
      const endDate = _.cloneDeep(selectedDate).endOf(`${calendarView}`)

      eventParamsCache.current = {
        ...eventParamsCache.current,
        start_date: startDate.format('YYYY-MM-DD'),
        end_date: endDate.format('YYYY-MM-DD'),
      }
      tryCallApi(eventParamsCache.current)
    }
    //eslint-disable-next-line
  }, [calendarView, selectedDate, tryCallApi])

  useEffect(() => {
    handleSidebarData(events, selectedDate)
    //eslint-disable-next-line
  }, [events])

  useEffect(() => {
    const f = data => {
      removeEventViaID(dispatch, data.id)
    }
    GlobalEvents.on('reservation:cancel', f)

    return () => {
      GlobalEvents.off('reservation:cancel', f)
    }
  }, [dispatch])

  const handleSelectSlot = event => {
    stateCalEv.pop(event, {source: 'calendar'})
  }

  const handleTitleAccessor = e => {
    return e.reservation_uid
      ? `System Reservation: ${e.reservation_uid}`
      : `${e.resourceName} ${e.title}`
  }

  const handleCloseModal = e => {
    setModal({
      show: false,
      start: null,
      end: null,
    })
  }

  return (
    <div className="ap-reservation-calendar">
      <div className="ap-reservation-calendar__container">
        <Grid container>
          <Grid item md={sidebarDateClicked || realSearch ? 10 : 12}>
            <Calendar
              popup
              selectable
              date={selectedDate.toDate()}
              localizer={localizer}
              events={events.data}
              startAccessor="start"
              endAccessor="end"
              style={{height: 760}}
              view={
                calendarView === 'gantt' || calendarView === 'reservation' ? 'month' : calendarView
              }
              components={{
                toolbar: BigCalendarToolbar,
              }}
              // resources={resources.data}
              onSelectEvent={handleSelectSlot}
              titleAccessor={handleTitleAccessor}
              //update the styling of the event on this part
              formats={{eventTimeRangeFormat: () => null}}
              onNavigate={date => {
                handleSidebarData(events, date)
                setSidebarDateClicked(true)
              }}
              eventPropGetter={(event, start, end, isSelected) => {
                let newStyle = {
                  backgroundColor: event.bgColor,
                  border: '#4d4d4d solid 1px',
                  borderLeft: '#4d4d4d solid 2px',
                  fontSize: '14px',
                }
                return {
                  className: '',
                  style: newStyle,
                }
              }}

              // resourceIdAccessor="resourceId"
            />
          </Grid>
          <CalendarRightSidebar prepareParamsForApi={prepareParamsForApi} />
        </Grid>
      </div>

      {stateCalEv.props.data.type === 'SystemReservationAction' && (
        <CalendarEventReservationPopup {...stateCalEv.props} onClose={e => stateCalEv.close()} />
      )}
      {stateCalEv.props.data.type === 'GroupDateField' && (
        <CalendarEventGroupDatePopup {...stateCalEv.props} onClose={e => stateCalEv.close()} />
      )}
      {stateCalEv.props.data.type === 'ReservationAction' && (
        <CalendarEventDateDrivenActionPopup
          {...stateCalEv.props}
          onClose={e => stateCalEv.close()}
        />
      )}
      {stateCalEv.props.data.type === 'ActionDateField' && (
        <CalendarEventActionDatePopup {...stateCalEv.props} onClose={e => stateCalEv.close()} />
      )}
    </div>
  )
}

export default ReservationCalendar
